import jsPDF from 'jspdf'
import QRCode from 'qrcode'

export interface PackageForPrint {
  packageTypeId: number
  name: string
  length: number
  width: number
  height: number
  weight?: number
}

export interface PrintOptions {
  layout: 'Single_4x6' | 'Grid_2x2' | 'Grid_3x3' | 'Avery_5160' | 'Avery_5163' | 'FullPage'
  quantity: number
  includeLabels: boolean
}

export interface QRCodeData {
  packageTypeId: number
  name: string
  qrCodeDataUrl: string
  dimensions: string
}

export class PDFGenerationService {

  async generateQRCodesAndPDF(packages: PackageForPrint[], options: PrintOptions): Promise<string> {
    // Generate QR codes for all packages
    const qrCodes: QRCodeData[] = []

    for (const pkg of packages) {
      for (let i = 0; i < options.quantity; i++) {
        const qrData = this.generateQRCodeData(pkg)
        const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
          width: 200,
          margin: 1,
          errorCorrectionLevel: 'H'
        })

        qrCodes.push({
          packageTypeId: pkg.packageTypeId,
          name: pkg.name,
          qrCodeDataUrl,
          dimensions: `${pkg.length}" × ${pkg.width}" × ${pkg.height}"`
        })
      }
    }

    // Generate PDF with the specified layout
    return this.generatePDF(qrCodes, options)
  }

  private generateQRCodeData(packageType: PackageForPrint): string {
    // Generate scanner-friendly format: PKG{version}L{length}W{width}H{height}T{timestamp}C{checksum}
    // This format uses only alphanumeric characters to work with barcode scanners that strip special characters

    const timestamp = Math.floor(Date.now() / 1000)
    const checksum = this.calculateChecksum(packageType)

    // Round dimensions to 1 decimal place for compact format
    const length = Math.round(packageType.length * 10) / 10
    const width = Math.round(packageType.width * 10) / 10
    const height = Math.round(packageType.height * 10) / 10

    // Format: PKG{version}L{length}W{width}H{height}T{timestamp}C{checksum}
    const qrData = `PKG10L${length}W${width}H${height}T${timestamp}C${checksum}`

    console.log('Generated scanner-friendly QR code for package', packageType.packageTypeId, ':', qrData)

    return qrData
  }

  private calculateChecksum(pkg: PackageForPrint): string {
    const data = `${pkg.packageTypeId}${pkg.name}${pkg.length}${pkg.width}${pkg.height}`
    let hash = 0
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16).substring(0, 8)
  }

  private async generatePDF(qrCodes: QRCodeData[], options: PrintOptions): Promise<string> {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter'
    })

    const pageWidth = 8.5
    const pageHeight = 11
    const margin = 0.5

    let currentPage = 1
    let currentIndex = 0

    while (currentIndex < qrCodes.length) {
      if (currentPage > 1) {
        pdf.addPage()
      }

      const codesOnThisPage = this.addPageContent(pdf, qrCodes.slice(currentIndex), options, pageWidth, pageHeight, margin)
      currentIndex += codesOnThisPage
      currentPage++
    }

    return pdf.output('datauristring')
  }

  private addPageContent(
    pdf: jsPDF,
    qrCodes: QRCodeData[],
    options: PrintOptions,
    pageWidth: number,
    pageHeight: number,
    margin: number
  ): number {
    const layout = this.getLayoutConfig(options.layout)

    // Use specific measurements for Avery labels
    let availableWidth = pageWidth - (2 * margin)
    let availableHeight = pageHeight - (2 * margin)
    let titleOffset = 0.5

    // Adjust for Avery label layouts with proper margins
    if (options.layout === 'Avery_5160') {
      // Avery 5160: 2.625" x 1" labels with specific margins
      availableWidth = pageWidth - 0.5 // Smaller side margins
      availableHeight = pageHeight - 0.5 // Smaller top/bottom margins
      titleOffset = 0.3 // Smaller title offset
    } else if (options.layout === 'Avery_5163') {
      // Avery 5163: 4" x 2" labels with specific margins
      availableWidth = pageWidth - 0.5
      availableHeight = pageHeight - 0.5
      titleOffset = 0.3
    }

    const cellWidth = availableWidth / layout.cols
    const cellHeight = availableHeight / layout.rows

    const maxCodesOnPage = layout.cols * layout.rows
    const codesToProcess = Math.min(qrCodes.length, maxCodesOnPage)

    // Add title (smaller for Avery layouts)
    const titleSize = (options.layout.startsWith('Avery_')) ? 12 : 16
    pdf.setFontSize(titleSize)
    pdf.text('QR Codes', pageWidth / 2, margin + titleOffset, { align: 'center' })

    for (let i = 0; i < codesToProcess; i++) {
      const qrCode = qrCodes[i]
      const row = Math.floor(i / layout.cols)
      const col = i % layout.cols

      const x = (pageWidth - availableWidth) / 2 + (col * cellWidth)
      const y = margin + titleOffset + 0.2 + (row * cellHeight)

      this.addQRCodeToCell(pdf, qrCode, x, y, cellWidth, cellHeight, options.includeLabels)
    }

    return codesToProcess
  }

  private addQRCodeToCell(
    pdf: jsPDF,
    qrCode: QRCodeData,
    x: number,
    y: number,
    width: number,
    height: number,
    includeLabels: boolean
  ): void {
    // Adjust QR code size based on layout - smaller for Avery labels
    let qrSizeRatio = 0.7
    let heightRatio = 0.6
    let topPadding = 0.1

    // Optimize for Avery label sizes
    if (height < 1.5) { // Small labels like Avery 5160
      qrSizeRatio = 0.85
      heightRatio = 0.75
      topPadding = 0.05
    }

    const qrSize = Math.min(width * qrSizeRatio, height * heightRatio)
    const qrX = x + (width - qrSize) / 2
    const qrY = y + topPadding

    // Add QR code image
    pdf.addImage(qrCode.qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize)

    if (includeLabels) {
      // Add package name with bold styling and increased size
      const nameSize = height < 1.5 ? 8 : 10
      pdf.setFontSize(nameSize)
      pdf.setFont('helvetica', 'bold') // Make name bold
      const nameY = qrY + qrSize + 0.1
      pdf.text(qrCode.name, x + width / 2, nameY, {
        align: 'center',
        maxWidth: width * 0.95
      })

      // Add dimensions with normal weight and smaller font
      pdf.setFont('helvetica', 'normal') // Reset to normal weight
      const dimSize = height < 1.2 ? 4 : 6 // More aggressive size reduction only for very small labels
      pdf.setFontSize(dimSize)
      const dimY = nameY + (height < 1.2 ? 0.08 : 0.12)
      if (dimY < y + height - 0.03) { // More lenient fit check
        pdf.text(qrCode.dimensions, x + width / 2, dimY, { align: 'center' })
      }
    }
  }

  private getLayoutConfig(layout: PrintOptions['layout']) {
    switch (layout) {
      case 'Single_4x6':
        return { cols: 1, rows: 1 }
      case 'Grid_2x2':
        return { cols: 2, rows: 2 }
      case 'Grid_3x3':
        return { cols: 3, rows: 3 }
      case 'Avery_5160':
        return { cols: 3, rows: 10 }
      case 'Avery_5163':
        return { cols: 2, rows: 5 }
      case 'FullPage':
        return { cols: 1, rows: 1 }
      default:
        return { cols: 3, rows: 3 }
    }
  }

  async downloadPDF(pdfDataUri: string, filename: string): Promise<void> {
    try {
      // Method 1: Try direct data URI download
      const link = document.createElement('a')
      link.href = pdfDataUri
      link.download = filename

      // Set additional attributes to force download
      link.setAttribute('download', filename)
      link.style.display = 'none'

      // Add to DOM temporarily
      document.body.appendChild(link)

      // Trigger download
      link.click()

      // Clean up
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link)
        }
      }, 100)
    } catch (error) {
      console.warn('Direct download failed, trying blob method:', error)

      try {
        // Method 2: Convert to blob and create blob URL
        const response = await fetch(pdfDataUri)
        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()

        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link)
          }
          URL.revokeObjectURL(blobUrl)
        }, 100)
      } catch (blobError) {
        console.error('Both download methods failed:', blobError)
        // Fallback: open in new tab
        window.open(pdfDataUri, '_blank')
      }
    }
  }

  async printPDF(pdfDataUri: string): Promise<void> {
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print QR Codes</title>
          <style>
            body { margin: 0; }
            iframe { width: 100%; height: 100vh; border: none; }
          </style>
        </head>
        <body>
          <iframe src="${pdfDataUri}"></iframe>
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print()
                window.onafterprint = function() {
                  window.close()
                }
              }, 500)
            }
          </script>
        </body>
        </html>
      `)
      printWindow.document.close()
    }
  }
}